import { useState } from 'react'

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Menu, Switch, Text, TextInput } from '@mantine/core'
import { SlidersHorizontal, GripVertical, Search } from 'lucide-react'

import DNButton from 'src/components/Buttons/Button/DNButton'

export interface ColumnConfig {
  key: string
  label: string
  visible: boolean
}

interface ColumnDisplayMenuProps {
  columns: ColumnConfig[]
  onColumnVisibilityChange: (columnKey: string, visible: boolean) => void
  onColumnReorder?: (newOrder: string[]) => void
}

// Sortable column item component
const SortableColumnItem = ({
  column,
  onVisibilityChange,
}: {
  column: ColumnConfig
  onVisibilityChange: (columnKey: string, visible: boolean) => void
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: column.key })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex w-full flex-row items-center justify-between ${
        isDragging ? 'bg-gray-100' : ''
      }`}
    >
      <div className="flex items-center gap-2">
        <div
          {...attributes}
          {...listeners}
          className="cursor-grab text-gray-400 hover:text-gray-600 active:cursor-grabbing"
        >
          <GripVertical size={12} />
        </div>
        <Text className="py-1 text-xs font-medium text-doubleNickel-gray-800">
          {column.label}
        </Text>
      </div>
      <Switch
        size="xs"
        checked={column.visible}
        onChange={(event) =>
          onVisibilityChange(column.key, event.currentTarget.checked)
        }
      />
    </div>
  )
}

const ColumnDisplayMenu = ({
  columns,
  onColumnVisibilityChange,
  onColumnReorder,
}: ColumnDisplayMenuProps) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const [search, setSearch] = useState('')

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (active.id !== over?.id && onColumnReorder) {
      const oldIndex = columns.findIndex((item) => item.key === active.id)
      const newIndex = columns.findIndex((item) => item.key === over.id)

      const newOrder = arrayMove(columns, oldIndex, newIndex).map(
        (col) => col.key
      )
      onColumnReorder(newOrder)
    }
  }
  return (
    <Menu shadow="md" position="bottom-end" width={250}>
      <Menu.Target>
        <DNButton variant="outline">
          <SlidersHorizontal
            size={14}
            className="fill-none stroke-doubleNickel-gray-700"
          />
          Display
          <div className="rounded-lg bg-doubleNickel-gray-100 px-1 py-0.5 text-[10px] text-doubleNickel-gray-500">
            {columns.filter((c) => c.visible).length}
          </div>
        </DNButton>
      </Menu.Target>
      <Menu.Dropdown className="rounded-lg">
        <div className="flex flex-col gap-2 py-2">
          <div className="flex flex-row items-center pr-4 text-sm">
            <Menu.Label>Display</Menu.Label>
          </div>
          <div className="px-3">
            <TextInput
              size="xs"
              placeholder="Search columns..."
              leftSection={<Search size={14} />}
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
            />
          </div>
          <div className="max-h-96 w-full space-y-3 overflow-y-auto px-3 text-xs">
            {(() => {
              const filteredColumns = columns.filter((col) =>
                col.label.toLowerCase().includes(search.toLowerCase())
              )
              return (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={filteredColumns.map((col) => col.key)}
                    strategy={verticalListSortingStrategy}
                  >
                    {filteredColumns.map((column) => (
                      <SortableColumnItem
                        key={column.key}
                        column={column}
                        onVisibilityChange={onColumnVisibilityChange}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              )
            })()}
          </div>
        </div>
      </Menu.Dropdown>
    </Menu>
  )
}

export default ColumnDisplayMenu
